import React, { useContext, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  InputLabel,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import MDEditor from '@uiw/react-md-editor';
import { parseISO } from 'date-fns';
import ukLocale from 'date-fns/locale/en-GB';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import { AuthContext, checkTokenStatus } from '../../App';
import {
  addNewQuestion,
  fetchQuestionById,
  selectQuestionsByOutcome
} from '../../store/slices/outcomeSlice';
import { selectSettingByName } from '../../store/slices/settingsSlice';
import { selectUserPreferences } from '../../store/slices/userSlice';
import InfoTooltip from './InfoTooltip';
import RecommendQuestions from './RecommendQuestions';

export default function FreeformQuestionForm({ outcome, setErrorMessage }) {
  const { userData } = useContext(AuthContext);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const maxQuestionLength = 2000;
  const maxResolutionCriteriaLength = 3000;
  const maxExtraInfoLength = 3000;
  const maxSourceLength = 200;
  const { setIsLoggedIn } = useContext(AuthContext);

  var localQuestionObj = JSON.parse(
    localStorage.getItem(
      'freeformquestion:' + outcome.id.toString() + userData.username
    )
  );
  if (localQuestionObj === null) {
    localQuestionObj = {};
  }
  const [isMounted, setIsMounted] = useState(false);
  const [questionText, setQuestionText] = useState(
    localQuestionObj.questionText ? localQuestionObj.questionText : 'Will '
  );
  const [resolutionCriteria, setResolutionCriteria] = useState(
    localQuestionObj.resolutionCriteria
      ? localQuestionObj.resolutionCriteria
      : ''
  );
  const [source, setSource] = useState(
    localQuestionObj.source ? localQuestionObj.source : ''
  );
  const [extraInfo, setExtraInfo] = useState(
    localQuestionObj.extraInfo ? localQuestionObj.extraInfo : ''
  );

  const [addRequestStatus, setAddRequestStatus] = useState('idle');

  const lastAllowedDate = !outcome.continue_forecasting
    ? parseISO(outcome.end_at)
    : null;

  const [resolutionDate, setResolutionDate] = useState(
    localQuestionObj.resolutionDate
      ? parseISO(localQuestionObj.resolutionDate)
      : parseISO(outcome.end_at)
  );
  const outcomeQuestions = useSelector((state) =>
    selectQuestionsByOutcome(state, outcome.id)
  );

  const userPreferences = useSelector((state) => selectUserPreferences(state));

  const changeQuestionText = (event) => {
    updateLocalStorage('questionText', event.target.value);
    setQuestionText(event.target.value);
  };
  const changeSource = (event) => {
    updateLocalStorage('source', event.target.value);
    setSource(event.target.value);
  };
  const changeResolutionCriteria = (resolution) => {
    updateLocalStorage('resolutionCriteria', resolution);
    setResolutionCriteria(resolution);
  };
  const changeExtraInfo = (event) => {
    updateLocalStorage('extraInfo', event.target.value);
    setExtraInfo(event.target.value);
  };
  const changeResolutionDate = (newValue) => {
    updateLocalStorage('resolutionDate', newValue);
    setResolutionDate(newValue);
  };

  const isDarkMode = localStorage.getItem('mode') === 'dark';

  const updateLocalStorage = (field, value) => {
    // outcome id + username is the composite unique field for items saved in local storage (this makes drafts unique per outcome per user)
    // object is saved in stringified format so we parse it after fetching
    var localQuestionObj = JSON.parse(
      localStorage.getItem(
        'freeformquestion:' + outcome.id.toString() + userData.username
      )
    );
    if (localQuestionObj === null) {
      localQuestionObj = {};
    }
    // update question object's specified field
    localQuestionObj[field] = value;
    // save updated object in local storage (stringified format)
    localStorage.setItem(
      'freeformquestion:' + outcome.id.toString() + userData.username,
      JSON.stringify(localQuestionObj)
    );
  };

  const openQuestionView = (id) => {
    navigate(`/questions/${id}`);
  };

  const recommendationSetting = useSelector((state) =>
    selectSettingByName(state, 'Recommender Toggle')
  );

  const checkQuestionDuplicate = () => {
    let duplicate = false;
    outcomeQuestions.forEach((question) => {
      if (
        question.question_text === questionText &&
        question.resolution_criteria === resolutionCriteria &&
        question.source === source &&
        question.extra_info === extraInfo &&
        question.resolution_date === moment(resolutionDate).format('YYYY-MM-DD')
      ) {
        duplicate = true;
      }
    });
    return duplicate;
  };

  const canCreate =
    [
      questionText,
      questionText !== 'Will ',
      resolutionCriteria,
      source,
      outcome.id,
      questionText.length <= maxQuestionLength,
      source.length <= maxSourceLength,
      resolutionCriteria.length <= maxResolutionCriteriaLength,
      extraInfo.length <= maxExtraInfoLength,
      checkTokenStatus(),
      !checkQuestionDuplicate()
    ].every(Boolean) && addRequestStatus === 'idle';

  const submitQuestion = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (canCreate) {
      setIsMounted(true);
      try {
        setAddRequestStatus('pending');
        const token = localStorage.getItem('auth_token');
        let payload = {
          question_text: questionText,
          resolution_criteria: resolutionCriteria,
          source: source,
          resolution_date: moment(resolutionDate).format('YYYY-MM-DD'),
          extra_info: extraInfo,
          outcome_id: outcome.id,
          auth_token: token
        };
        await dispatch(addNewQuestion(payload))
          .unwrap()
          .then((response) => {
            if (isMounted) {
              // clears local storage 'draft' when question is submitted
              localStorage.removeItem(
                'freeformquestion:' + outcome.id.toString() + userData.username
              );
              setQuestionText('Will ');
              setResolutionCriteria('');
              setSource('');
              setResolutionCriteria('');
              setExtraInfo('');
              setResolutionDate(parseISO(outcome.end_at));
            }
            if (response.status === 'success') {
              if (typeof response.data.id !== 'undefined') {
                dispatch(
                  fetchQuestionById({
                    questionId: response.data.id,
                    auth_token: token
                  })
                );
                openQuestionView(response.data.id);
              }
            }
          });
      } catch (err) {
        setErrorMessage(`Failed to create question: ${err.message}`);
      } finally {
        if (isMounted) setAddRequestStatus('idle');
        setIsMounted(false);
      }
    } else if (checkTokenStatus() === false) {
      setIsLoggedIn(false);
      return <Navigate to={'/login'} />;
    } else if (checkQuestionDuplicate() === true) {
      setErrorMessage(
        'A question identical to this one already exists, please provide a different question.'
      );
    } else if (!questionText) {
      setErrorMessage('Please provide the question text.');
    } else if (questionText === 'Will ') {
      setErrorMessage('Please provide the question text.');
    } else if (questionText.length > maxQuestionLength) {
      setErrorMessage(
        'Please ensure the question text does not exceed the maximum characters.'
      );
    } else if (!resolutionCriteria) {
      setErrorMessage('Please provide the resolution criteria.');
    } else if (resolutionCriteria.length > maxResolutionCriteriaLength) {
      setErrorMessage(
        'Please ensure the resolution criteria does not exceed the maximum characters.'
      );
    } else if (!source) {
      setErrorMessage('Please provide the resolution source authority.');
    } else if (source.length > maxSourceLength) {
      setErrorMessage(
        'Please ensure the source does not exceed the maximum characters.'
      );
    } else if (extraInfo.length > maxExtraInfoLength) {
      setErrorMessage(
        'Please ensure the extra info does not exceed the maximum characters.'
      );
    } else if (!resolutionDate) {
      setErrorMessage('Please provide the resolution date.');
    } else if (!outcome.id) {
      setErrorMessage('An error occurred, please refresh the page.');
    } else if (addRequestStatus !== 'idle') {
      setErrorMessage('Request has been sent to the server, please wait.');
    } else {
      setErrorMessage('Question could not be created.');
    }
  };

  return (
    <div className="flex-col">
      <div className="w-full p-1">
        <div className="flex">
          <InputLabel htmlFor="question-text">
            What is your question?
          </InputLabel>
        </div>
        <div className="flex">
          <TextField
            type="text"
            variant="outlined"
            multiline
            value={questionText}
            placeholder="e.g. 'Will scenario X occur?'."
            minRows="1"
            onChange={(event) => changeQuestionText(event)}
            inputProps={{ style: { fontSize: '0.875rem' } }}
            helperText={`${questionText.length}/${maxQuestionLength}`}
            error={questionText.length > maxQuestionLength}
            id="question-text"
            className="w-full"></TextField>
          <div className="ml-1">
            <InfoTooltip text="A text description of the question, in the form of a scenario, e.g. 'Will scenario X occur?'." />
          </div>
        </div>
      </div>
      {recommendationSetting?.active && (
        <RecommendQuestions questionText={questionText} />
      )}
      <div className="w-full p-1">
        <div>
          <InputLabel htmlFor="resolution-criteria">
            What is the criteria for resolution of the question?
          </InputLabel>
        </div>
        <div className="flex" data-color-mode={isDarkMode ? 'dark' : 'light'}>
          <MDEditor
            id="resolution-criteria"
            value={resolutionCriteria}
            className="w-full"
            onChange={(event) => changeResolutionCriteria(event)}
            textareaProps={{
              placeholder:
                'i.e. the process by which it will be determined whether the outcome described occured.',
              maxLength: maxResolutionCriteriaLength,
              required: true,
              autoCorrect: 'on'
            }}
            preview="edit"
            style={{
              backgroundColor: isDarkMode
                ? theme.palette.background.paper
                : '#fff',
              color: isDarkMode ? theme.palette.text.primary : '#000'
            }}
            defaultTabEnable={true}
          />
          <div className="ml-1">
            <InfoTooltip text="A description of the resolution criteria for the question, i.e. the process by which it will be determined whether the outcome described occured." />
          </div>
        </div>
        <Typography
          color="text.secondary"
          sx={{
            fontSize: '0.75rem'
          }}>{`${resolutionCriteria.length}/${maxResolutionCriteriaLength}`}</Typography>
      </div>
      <div className="w-full flex">
        <div className="w-full p-1">
          <div>
            <InputLabel htmlFor="source">
              Provide a Resolution Source Authority
            </InputLabel>
          </div>
          <div className="flex">
            <TextField
              type="text"
              variant="outlined"
              value={source}
              placeholder="e.g. https://www.bbc.co.uk/news"
              onChange={(event) => changeSource(event)}
              helperText={`${source.length}/${maxSourceLength}`}
              error={source.length > maxSourceLength}
              id="source"
              inputProps={{ style: { fontSize: '0.875rem' } }}
              className="w-full"></TextField>
            <div className="ml-1">
              <InfoTooltip text="A link to the authority that will be used to decide resolution." />
            </div>
          </div>
        </div>
        <div>
          <div>
            <InputLabel htmlFor="resolution-date">
              When will the Question Resolve?
            </InputLabel>
          </div>
          <div className="flex w-full justify-start flex-basis: auto">
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ukLocale}>
              <DatePicker
                clearable
                label="Resolution Date"
                value={resolutionDate}
                id="resolution-date"
                onChange={(newValue) => {
                  changeResolutionDate(newValue);
                }}
                textField={(params) => <TextField size="small" {...params} />}
                minDate={parseISO(outcome.forecasting_start_date)}
                maxDate={lastAllowedDate}
              />
            </LocalizationProvider>
            <div className="ml-1">
              <InfoTooltip text="A date by which the question will definitely have resolved, i.e. be known to have occured or not occured." />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full p-1">
        <div>
          <InputLabel htmlFor="extra-info">
            Optional extra information
          </InputLabel>
        </div>
        <div className="flex">
          <TextField
            type="text"
            variant="outlined"
            value={extraInfo}
            placeholder="Extra information."
            minRows="1"
            onChange={(event) => changeExtraInfo(event)}
            inputProps={{ style: { fontSize: '0.875rem' } }}
            helperText={`${extraInfo.length}/${maxExtraInfoLength}`}
            error={extraInfo.length > maxExtraInfoLength}
            id="extra-info"
            className="w-full"></TextField>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          variant="contained"
          disabled={isMounted}
          onClick={(event) => submitQuestion(event)}
          className="font-bold rounded">
          {isMounted ? (
            <ClipLoader color="#ffffff" loading={true} size={50} />
          ) : (
            'Post'
          )}
        </Button>
      </div>
    </div>
  );
}
